import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import SalesTrackerStore from './../../store'
import MaxAvDateStore from './../../store/max-av-date.store'
import { MDBRow, MDBCol, MDBIcon } from 'mdb-react-ui-kit'
import PointOverrides from './../PointOverrides/PointOverrides.component'
import PolicyCommission from '../PolicyCommission/PolicyCommission.component'
import { UIDropdown, UIInput, UIDatePickerInput } from './../../../../components/forms/form-fields'
import appConstants from './../../../../constants/appConstants'
import AV_STATUSES from './../../../../constants/av-status.constants'
import moment from 'moment'
import { toast } from 'react-toastify'
import preventInputUnicodeCharacters from '../../../../shared/utilities/preventInputUnicodeCharacters.function'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import TermLengthsService from '../../../../shared/services/TermLengths.service'
import AvService from '../../../../shared/services/Av.service'

import './AvPolicyForm.component.scss'

const TermLengths = appConstants.termLength.map(item => ({
    ...item,
    value: `${item.value}`,
  })),
  AppStatusList = AV_STATUSES,
  TRANSFER_W_PTS = 'Transfer w Pts'

const toDate = dateStr => {
  if (dateStr) {
    if (typeof dateStr === 'string') return moment(`${dateStr}`.split('T').shift()).toDate()
    if (typeof dateStr === 'object') return dateStr
  }
}

const AvPolicyForm = ({ Policy, idx }) => {
  const avId = Policy?.id(),
    avCoverageId = Policy?.get('coverage_id'),
    avTermLen = Policy?.get('av_term_length'),
    isStaff = UserProfileService.isA(['internal-admin', 'internal-staff', 'agency-owner', 'system-admin'], true),
    { isAgeRestricted } = MaxAvDateStore,
    [trackAcaLives, setTrackAcaLives] = useState(null),
    [trackGroupLives, setTrackGroupLives] = useState(null),
    [sourceCoverageId, setSourceCoverageId] = useState(null),
    [sourceCarrierId, setSourceCarrierId] = useState(null),
    [savedPolicy, setSavedPolicy] = useState(null),
    [transferEligible, setTransferEligible] = useState(false),
    [isUpdating, setIsUpdating] = useState({ carrier: false }),
    [avEffDate, setAvEffDate] = useState(null),
    [avTermLengthMax, setAvTermLengthMax] = useState(null),
    [avTermLengthMin, setAvTermLengthMin] = useState(null),
    [permitTermLength, setPermitTermLength] = useState(false),
    [coverage, setCoverage] = useState(null),
    [carrierTermMaxLength, setCarrierTermMaxLength] = useState(null),
    [carrierTermMinLength, setCarrierTermMinLength] = useState(null),
    [carrierTermDefaultLength, setCarrierTermDefaultLength] = useState(null),
    policyAvEffDate = Policy?.get('av_eff_date'),
    isPreviousPlicy = SalesTrackerStore.isPreviousPolicy,
    previousPolicyHasEffDate = SalesTrackerStore.previousPolicyHasEffDate

  useEffect(() => {
    if (avEffDate === null && policyAvEffDate) setAvEffDate(toDate(policyAvEffDate))
    else if (avEffDate && !policyAvEffDate) setAvEffDate(null)
    else {
      const a =
          avEffDate && typeof avEffDate === 'object'
            ? moment(avEffDate).format('YYYY-MM-DD')
            : (avEffDate ? `${avEffDate}` : '').split('T').shift(),
        p =
          policyAvEffDate && typeof policyAvEffDate === 'object'
            ? moment(policyAvEffDate).format('YYYY-MM-DD')
            : (policyAvEffDate ? `${policyAvEffDate}` : '').split('T').shift()
      if (a && p && /\d{4}-\d{2}-\d{2}/.test(a) && /\d{4}-\d{2}-\d{2}/.test(p) && a !== p)
        setAvEffDate(toDate(policyAvEffDate))
      else if (avEffDate && !policyAvEffDate) setAvEffDate(null)
      else {
        const a =
            avEffDate && typeof avEffDate === 'object'
              ? moment(avEffDate).format('YYYY-MM-DD')
              : (avEffDate ? `${avEffDate}` : '').split('T').shift(),
          p =
            policyAvEffDate && typeof policyAvEffDate === 'object'
              ? moment(policyAvEffDate).format('YYYY-MM-DD')
              : (policyAvEffDate ? `${policyAvEffDate}` : '').split('T').shift()
        if (a && p && /\d{4}-\d{2}-\d{2}/.test(a) && /\d{4}-\d{2}-\d{2}/.test(p) && a !== p)
          setAvEffDate(toDate(policyAvEffDate))
      }
    }
  }, [avEffDate, policyAvEffDate])

  useEffect(() => {
    if (!avTermLengthMax && !avTermLengthMin && avCoverageId) setAvTermLength(avCoverageId)
  }, [avId, avCoverageId, avTermLengthMax, avTermLengthMin])

  // Set initial carrier min / max term length when a policy is being updated AND the carrier is already set.
  const carrierId = Policy.get('carrier_id')
  useEffect(() => {
    if (avId && carrierId && !carrierTermMaxLength && !carrierTermMinLength) {
      setCarrierTermMaxLength(getCarrier()?.get('c_term_length_max'))
      setCarrierTermMinLength(getCarrier()?.get('c_term_length_min'))
      setCarrierTermDefaultLength(getCarrier()?.get('c_term_length'))
    }
  }, [avId, carrierId, carrierTermMaxLength, carrierTermMinLength])

  // Set point override options when editing an existing policy.
  // This fixes a race condition where fetchById calls setPointOverrideOptions
  // before PointOverrides/Coverages/Carriers are loaded from the API.
  const [pointOverridesInitialized, setPointOverridesInitialized] = useState(false)
  useEffect(() => {
    if (pointOverridesInitialized) return
    if (avId && carrierId && avCoverageId) {
      const Coverage = getCoverage()
      const Carrier = getCarrier()
      if (Coverage && Carrier && SalesTrackerStore.PointOverrides.length > 0) {
        setPointOverrideOptions(idx)
        setPointOverridesInitialized(true)
      }
    }
  })

  // Update trackAcaLives when coverage_id or carrier_id changes
  // This effect will also re-run when the component re-renders due to MobX store updates
  // (since the component is wrapped with observer)
  useEffect(() => {
    const coverageId = Policy.get('coverage_id')
    const currentCarrierId = Policy.get('carrier_id')
    if (coverageId && currentCarrierId) {
      // Check if coverage data is available - if not, setShouldTrackLives will keep trackAcaLives as null
      // so we can retry on next render when coverage data loads
      const Coverage = getCoverage()
      if (Coverage) {
        // Coverage is available, set trackAcaLives based on coverage settings
        setShouldTrackLives()
      } else {
        if (trackAcaLives === null && SalesTrackerStore.Coverages.length > 0) {
          // Coverage data has loaded but this specific coverage isn't found
          // Set to false since coverage should be available but isn't matching
          setTrackAcaLives(false)
        }
        if (trackGroupLives === null && SalesTrackerStore.Coverages.length > 0) {
          // Coverage data has loaded but this specific coverage isn't found
          // Set to false since coverage should be available but isn't matching
          setTrackGroupLives(false)
        }
      }

      // If Coverage is null and Coverages.length is 0, keep trackAcaLives as null
      // so the initial check (line 379) can retry when data loads
    }
  }, [avCoverageId, carrierId])

  const currencyKeypress = event => {
    const inputValue = Policy.get(event.target.name) || ''
    const key = event.key

    const isNumeric = key >= '0' && key <= '9'
    const isDecimal = key === '.'
    const decimalExists = `${inputValue}`.includes('.')

    // Allow navigation keys and control keys (backspace, delete, etc.)
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
      return true
    }

    // Prevent first 0
    if (inputValue === '0' && isNumeric) {
      event.preventDefault()
      Policy.set(event.target.name, event.key)
      return false
    }

    // Prevent initial decimal point without leading zero
    if (inputValue === '' && isDecimal) {
      event.preventDefault()
      return false
    }

    // Prevent leading zeroes unless followed by a decimal point
    if (`${inputValue}`.startsWith('0') && !`${inputValue}`.includes('.') && isNumeric) {
      event.preventDefault()
      return false
    }

    // Allow numeric input if valid
    if (isNumeric) {
      // Allow input if no decimal exists or if there are less than 2 decimal places
      if (!decimalExists || (decimalExists && `${inputValue}`.split('.')[1].length < 2)) {
        return true
      }
    }

    // Allow decimal input if no decimal exists yet
    if (isDecimal && !decimalExists) {
      return true
    }

    // Prevent invalid input
    event.preventDefault()
    return false
  }

  const getCarrier = () => SalesTrackerStore.getCarrier(Policy.get('carrier_id'))

  const getCoverage = () => SalesTrackerStore.getCoverage(Policy.get('coverage_id'))

  const getCarrierOptions = () => {
    let userId = SalesTrackerStore.AOR.sellingAgentId,
      Carriers = SalesTrackerStore.Carriers
    Carriers = Carriers && Array.isArray(Carriers) ? Carriers : []

    // Only permit `active` carriers
    Carriers = Carriers.filter(Carrier => parseInt(Carrier.get('c_active')) === 1)

    // Only permit carriers the agent is contracted with, or the carrier previously set to this policy.
    if (userId && Array.isArray(SalesTrackerStore.agentContracts[userId]))
      Carriers = Carriers.filter(
        Carrier =>
          (sourceCarrierId && parseInt(sourceCarrierId) === parseInt(Carrier.id())) ||
          ~(SalesTrackerStore.agentContracts[userId] || []).indexOf(parseInt(Carrier.id()))
      )

    // Only permit carriers where sales tracker is enabled.
    Carriers = Carriers.filter(
      Carrier => parseInt(Carrier.get('c_active')) === 1 && Carrier.get('visibility')?.tracker?.enable
    )

    // Sort carriers by name.
    Carriers = Carriers.sort((a, b) => a.get('c_name').localeCompare(b.get('c_name')))

    // Map the Carriers to an object for UISelect options.
    Carriers = Carriers.map(Carrier => ({
      text: Carrier.get('c_name'),
      secondaryText: Carrier.get('c_description'),
      value: `${Carrier.id()}`,
      checked: !!(carrier_id && parseInt(carrier_id) === parseInt(Carrier.id())),
    }))

    return Carriers
  }

  const setAvCarrier = () => {
    const Carrier = getCarrier()
    if (Carrier && Carrier.get('c_name') !== Policy.get('av_carrier')) Policy.set('av_carrier', Carrier.get('c_name'))
  }

  const setAvCoverage = () => {
    const Coverage = getCoverage()
    if (Coverage && Coverage.get('coverage_name') !== Policy.get('av_coverage'))
      Policy.set('av_coverage', Coverage.get('coverage_name'))
  }

  const setAvTermLength = async (coverageId, updPolicy) => {
    const coverageTermLength = (
      await TermLengthsService.search({
        pagination: false,
        search: { coverage_id: coverageId },
      })
    )?.models?.shift()

    if (updPolicy) {
      Policy.set(
        'av_term_length',
        coverageTermLength?.default_length ? coverageTermLength.default_length : carrierTermDefaultLength
      )
    }
    setAvTermLengthMax(coverageTermLength?.max_length ? coverageTermLength.max_length : carrierTermMaxLength)
    setAvTermLengthMin(coverageTermLength?.min_length ? coverageTermLength.min_length : carrierTermMinLength)

    // lockdown the Term Length if there are no viable options.
    setPermitTermLength(
      !(
        `${coverageTermLength?.max_length ? coverageTermLength.max_length : carrierTermMaxLength}` ===
        `${coverageTermLength?.min_length ? coverageTermLength.min_length : carrierTermMinLength}`
      )
    )
  }

  const updateCoverage = async (coverageId, idx) => {
    if (!coverageId) return

    Policy.set('coverage_id', parseInt(coverageId))
    Policy.set('pts_override_id', null)
    Policy.set('pts_override_val', null)
    Policy.set('aca_lives', 0)
    Policy.set('group_lives', 0)
    setAvTermLength(coverageId, true)
    setCoverage(parseInt(coverageId))
    setShouldTrackLives()
    setAvCoverage()
    setPointOverrideOptions(idx)
  }

  const updateCarrier = async (carrierId, idx) => {
    Policy.set('carrier_id', carrierId)
    Policy.set('av_term_length', null)
    Policy.set('coverage_id', null)
    Policy.set('pts_override_id', null)
    Policy.set('pts_override_val', null)
    Policy.set('aca_lives', 0)
    Policy.set('group_lives', 0)
    setCoverage(null)

    if (trackAcaLives === true) {
      SalesTrackerStore.setValidity(idx, 'aca_lives', true)
      setTrackAcaLives(false)
    }

    if (trackGroupLives === true) {
      SalesTrackerStore.setValidity(idx, 'group_lives', true)
      setTrackGroupLives(false)
    }

    if (!Array.isArray(SalesTrackerStore.CoveragesByCarrier[carrierId])) {
      setIsUpdating(prev => ({ ...prev, carrier: true }))
      await SalesTrackerStore.fetchCoveragesByCarrier(carrierId)
    }

    const selected_carrier = getCarrier()
    setAvTermLengthMax(selected_carrier?.get('c_term_length_max'))
    setAvTermLengthMin(selected_carrier?.get('c_term_length_min'))
    Policy.set('av_term_length', selected_carrier?.get('c_term_length'))
    setCarrierTermMaxLength(selected_carrier?.get('c_term_length_max'))
    setCarrierTermMinLength(selected_carrier?.get('c_term_length_min'))
    setCarrierTermDefaultLength(selected_carrier?.get('c_term_length'))

    setAvCarrier()
    // setPremiumLabel()
    setPointOverrideOptions(idx)
    setIsUpdating(prev => ({ ...prev, carrier: false }))
  }

  const setShouldTrackAcaLives = () => {
    const Coverage = getCoverage()

    if (Coverage) {
      if (parseInt(Coverage.id()) === 78 || parseInt(Coverage.id()) === 159) return setTrackAcaLives(true)
      if (parseInt(Coverage.get('track_aca_lives')) === 1) return setTrackAcaLives(true)
      // If coverage exists but doesn't meet the criteria, set to false
      return setTrackAcaLives(false)
    }
    // If coverage is not available yet, don't set trackAcaLives to false
    // This allows the effect to retry when coverage data loads
    // Only set to false if we've already tried and coverage should be available
    if (trackAcaLives !== null) {
      // Coverage data should be available but isn't, so set to false
      return setTrackAcaLives(false)
    }
    // trackAcaLives is still null, so we'll retry when coverage loads
  }

  const setShouldTrackGroupLives = () => {
    const Coverage = getCoverage()

    if (Coverage) {
      if (parseInt(Coverage.get('track_group_lives')) === 1) return setTrackGroupLives(true)
      // If coverage exists but doesn't meet the criteria, set to false
      return setTrackGroupLives(false)
    }
    // If coverage is not available yet, don't set trackGroupLives to false
    // This allows the effect to retry when coverage data loads
    // Only set to false if we've already tried and coverage should be available
    if (trackGroupLives !== null) {
      // Coverage data should be available but isn't, so set to false
      return setTrackGroupLives(false)
    }
    // trackGroupLives is still null, so we'll retry when coverage loads
  }

  const setShouldTrackLives = () => {
    setShouldTrackAcaLives()
    setShouldTrackGroupLives()
  }

  const onEvEffDateChange = value => {
    if (value) {
      const isValidDate = value instanceof Date && !isNaN(value.getTime())
      const currentYear = new Date().getFullYear()
      const selectedYear = value.getFullYear()
      // Check if the selected year is within the valid range (current year +/- 50 years)
      const isYearValid = selectedYear >= currentYear - 50 && selectedYear <= currentYear + 50
      if (isYearValid && isValidDate) {
        let valueStr = JSON.parse(JSON.stringify(value)).split('T').shift()
        if (valueStr !== Policy.get('av_eff_date')) {
          Policy.set('av_eff_date', valueStr)
          setAvEffDate(value)
        }
      }
    }
  }

  // const setPremiumLabel = () => {
  //   const Carrier = getCarrier()

  //   if (Carrier) {
  //     if (parseInt(Carrier.get('c_term_length_max')) === 1)
  //       return setAvPremiumLabel('Premium')
  //     else if (parseInt(Policy.get('av_term_length')) > 1)
  //       return setAvPremiumLabel('Monthly Premium')
  //     else return setAvPremiumLabel('Premium')
  //   }

  //   setAvPremiumLabel('Monthly Premium')
  // }

  const setPointOverrideOptions = idx => {
    SalesTrackerStore.setPointOverrideOptions(getCoverage(), getCarrier(), idx)
  }

  // Initialize trackAcaLives && trackGroupLives on first render if not set
  if (trackAcaLives === null || trackGroupLives === null) {
    const coverageId = Policy.get('coverage_id')
    const currentCarrierId = Policy.get('carrier_id')
    if (coverageId && currentCarrierId) setShouldTrackLives()
  }

  if (Policy) {
    if (!Policy.isNew()) {
      if (sourceCoverageId === null) setSourceCoverageId(parseInt(Policy.get('coverage_id')))
      if (sourceCarrierId === null) setSourceCarrierId(parseInt(Policy.get('carrier_id')))
    }
  }

  // Snapshot of the policy as saved, re-taken whenever the form moves to another AV.
  const savedAvId = Policy.isNew() ? null : avId,
    clientKey = (fname, lname) => `${fname || ''}`.trim().toLowerCase() + '|' + `${lname || ''}`.trim().toLowerCase()
  if (!savedPolicy || savedPolicy.avId !== savedAvId) {
    setSavedPolicy({
      avId: savedAvId,
      av_status: savedAvId ? Policy.get('av_status') : null,
      carrier_id: savedAvId ? Policy.get('carrier_id') : null,
      coverage_id: savedAvId ? Policy.get('coverage_id') : null,
      av_date: savedAvId && Policy.get('av_date') ? moment(toDate(Policy.get('av_date'))).format('YYYY-MM-DD') : null,
      client: savedAvId ? clientKey(Policy.get('av_client_fname'), Policy.get('av_client_lname')) : null,
    })
    setTransferEligible(false)
  }

  const coverage_id = Policy.get('coverage_id'),
    carrier_id = Policy.get('carrier_id'),
    userDetails = {},
    active_av_status = null

  // Client, agent & AV date are shared by every policy in the sale and live on the master policy.
  const MasterPolicy = SalesTrackerStore.Policy,
    transferCheck = {
      av_id: avId || null,
      selling_agent_id: SalesTrackerStore.AOR.sellingAgentId,
      contact_id: SalesTrackerStore.contactId,
      av_client_fname: `${MasterPolicy?.get('av_client_fname') || ''}`.trim(),
      av_client_lname: `${MasterPolicy?.get('av_client_lname') || ''}`.trim(),
      av_date: MasterPolicy?.get('av_date') ? moment(toDate(MasterPolicy.get('av_date'))).format('YYYY-MM-DD') : null,
      carrier_id,
      coverage_id,
    },
    // An AV saved as "Transfer (w Pts)" keeps the status, even after its rule changes, until its carrier, coverage, AV date or client changes.
    keepsSavedTransfer =
      savedPolicy?.av_status === TRANSFER_W_PTS &&
      `${savedPolicy.carrier_id}` === `${carrier_id}` &&
      `${savedPolicy.coverage_id}` === `${coverage_id}` &&
      savedPolicy.av_date === transferCheck.av_date &&
      savedPolicy.client === clientKey(transferCheck.av_client_fname, transferCheck.av_client_lname),
    showTransferStatus = transferEligible || keepsSavedTransfer,
    avStatus = Policy.get('av_status')

  // "Transfer (w Pts)" is only offered when a transfer rule matches the sale.
  useEffect(() => {
    const { contact_id, av_client_fname, av_client_lname, av_date, carrier_id } = transferCheck
    if (!carrier_id || !av_date || !(contact_id || (av_client_fname && av_client_lname)))
      return setTransferEligible(false)

    let isCancelled = false
    const timer = setTimeout(async () => {
      const result = await AvService.transferEligibility(transferCheck).catch(() => null)
      if (!isCancelled) setTransferEligible(!!result?.eligible)
    }, 400)

    return () => {
      isCancelled = true
      clearTimeout(timer)
    }
  }, Object.values(transferCheck))

  useEffect(() => {
    if (!showTransferStatus && avStatus === TRANSFER_W_PTS) Policy.set('av_status', null)
  }, [showTransferStatus, avStatus])

  const getCoverageOptions = carrierId => {
    let Coverages = SalesTrackerStore.CoveragesByCarrier[carrierId]
    Coverages = Array.isArray(Coverages) ? Coverages : []
    return Coverages.filter(
      Coverage =>
        parseInt(Coverage.get('coverage_active')) === 1 ||
        (sourceCoverageId && parseInt(sourceCoverageId) === parseInt(Coverage.id()))
    )
      .map(Coverage => {
        return {
          text: Coverage.get('coverage_name'),
          value: `${Coverage.id()}`,
          checked: !!(coverage_id && parseInt(coverage_id) === parseInt(Coverage.id())),
        }
      })
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  const showToastForIllegalCharacters = field => {
    toast.warn(
      `You tried to paste invalid text in ${field}, but we’ve attempted to correct it. Please review for accuracy.`,
      { position: toast.POSITION.TOP_RIGHT, autoClose: 7000 }
    )
  }

  return (
    <form className='AvPolicyFormComponent container-fluid'>
      <MDBRow>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIDropdown
            optionHeight={37}
            disablePortal
            searchLabel='Search'
            showSelectedSecondaryText
            label='Carrier'
            name='carrier_id'
            search
            disabled={(isAgeRestricted && !isStaff) || isUpdating?.carrier}
            className='av-carrier-selector'
            options={getCarrierOptions()}
            selected={Policy.get('av_carrier')}
            onChange={evt => {
              const value = parseInt(evt.target.value)
              if (value !== parseInt(Policy.get('carrier_id'))) {
                updateCarrier(value, idx)
              }
            }}
            required={true}
            value={Policy.get('carrier_id')}
            rules={{ required: true }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'carrier_id', validity.isValid)}
          />
        </MDBCol>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIDropdown
            optionHeight={37}
            disablePortal
            searchLabel='Search'
            label='Coverage Type'
            name='coverage_id'
            search={true}
            disabled={(isAgeRestricted && !isStaff) || isUpdating?.carrier}
            options={getCoverageOptions(Policy.get('carrier_id'))}
            onChange={evt => {
              const value = parseInt(evt.target.value)
              if (value && value !== parseInt(Policy.get('coverage_id'))) updateCoverage(value)
            }}
            required={true}
            value={Policy.get('coverage_id')}
            rules={{ required: true }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'coverage_id', validity.isValid)}
          />
        </MDBCol>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIInput
            label='Product'
            name='av_product'
            type='text'
            disabled={isAgeRestricted && !isStaff}
            onChange={evt => {
              const filteredResult = preventInputUnicodeCharacters(evt.target.value)
              if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Product')
              if (filteredResult.fixedString !== Policy.get('av_product'))
                Policy.set('av_product', filteredResult.fixedString)
            }}
            required={true}
            value={Policy.get('av_product')}
            rules={{ required: true, minLength: 2, maxLength: 255 }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_product', validity.isValid)}
            autoComplete='off'
          />
        </MDBCol>
        {/*
        [
          {Policy?.get('coverage_id') ? (`${avTermLengthMax}` === `${avTermLengthMin}` ? 12 : 'selector') : <>no coverage</>},
          {avTermLengthMax},
          {avTermLengthMin}
        ][
          {permitTermLength ? 'selector' : 12}
        ]
        */}
        {permitTermLength ? (
          <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
            <UIDropdown
              optionHeight={37}
              disablePortal
              searchLabel='Search'
              label='Term Length'
              name='av_term_length'
              disabled={isAgeRestricted && !isStaff}
              options={TermLengths.map(item => {
                return {
                  ...item,
                  checked:
                    Policy.get('av_term_length') !== null &&
                    parseInt(item.value) === parseInt(Policy.get('av_term_length')),
                }
              }).filter(item =>
                !avTermLengthMax || item.checked
                  ? item
                  : parseInt(avTermLengthMax) < parseInt(item.value)
                  ? false
                  : parseInt(avTermLengthMin) > parseInt(item.value)
                  ? false
                  : item
              )}
              onChange={evt => {
                if (parseInt(evt.target.value) !== parseInt(Policy.get('av_term_length')))
                  Policy.set('av_term_length', evt.target.value)
                // setPremiumLabel()
              }}
              required={true}
              value={Policy.get('av_term_length')}
              rules={{ required: true }}
              showValidity={SalesTrackerStore.showErrors}
              onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_term_length', validity.isValid)}
              search
            />
          </MDBCol>
        ) : (
          <></>
        )}
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIDropdown
            optionHeight={37}
            disablePortal
            searchLabel='Search'
            label='Application Status'
            name='av_status'
            disabled={isAgeRestricted && !isStaff}
            options={AppStatusList.filter(s => s.value !== TRANSFER_W_PTS || showTransferStatus).map(s => ({
              ...s,
              disabled:
                appConstants.agents.includes(userDetails && userDetails.usertype_id) &&
                active_av_status === 'Submitted' &&
                s.value === 'Withdrawn',
              checked: `${Policy.get('av_status')}`.trim().toLowerCase() === `${s.value}`.trim().toLowerCase(),
            }))}
            onChange={evt => {
              if (evt.target.value !== Policy.get('av_status')) Policy.set('av_status', evt.target.value)
            }}
            required={true}
            value={Policy.get('av_status')}
            rules={{ required: true }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_status', validity.isValid)}
            search
          />
        </MDBCol>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIInput
            label='Policy # or D.O.B.'
            name='av_appid'
            type='text'
            disabled={isAgeRestricted && !isStaff}
            onChange={evt => {
              const filteredResult = preventInputUnicodeCharacters(evt.target.value)
              if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Policy # or D.O.B.')
              if (filteredResult.fixedString !== Policy.get('av_appid'))
                Policy.set('av_appid', filteredResult.fixedString)
            }}
            autoComplete='off'
            required={true}
            value={Policy.get('av_appid')}
            rules={{ required: true, minLength: 2, maxLength: 255 }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_appid', validity.isValid)}
          />
        </MDBCol>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIInput
            label='Application Fee'
            name='av_fee'
            type='text'
            disabled={isAgeRestricted && !isStaff}
            onChange={evt => {
              const filteredResult = preventInputUnicodeCharacters(evt.target.value)
              if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Application Fee')
              if (filteredResult.fixedString !== Policy.get('av_fee'))
                Policy.set(
                  'av_fee',
                  isNaN(filteredResult.fixedString) || !filteredResult.fixedString ? '0' : filteredResult.fixedString
                )
            }}
            autoComplete='off'
            value={Policy.get('av_fee')}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_fee', validity.isValid)}
            onKeyPress={currencyKeypress}
          />
        </MDBCol>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIInput
            label={'Monthly Premium'}
            name='av_premium'
            type='text'
            disabled={isAgeRestricted && !isStaff}
            onChange={evt => {
              const filteredResult = preventInputUnicodeCharacters(evt.target.value)
              if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Monthly Premium')
              if (filteredResult.fixedString !== Policy.get('av_premium'))
                Policy.set('av_premium', filteredResult.fixedString)
            }}
            autoComplete='off'
            required={true}
            value={Policy.get('av_premium')}
            rules={{ required: true, minLength: 2, maxLength: 255 }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_premium', validity.isValid)}
            onKeyPress={currencyKeypress}
          />
          {coverage === 90 && (
            <small className='text-left helper-link'>
              For annuities, enter the <strong>monthly</strong> payment (Total premium lump-sum divided by 12).
            </small>
          )}
        </MDBCol>
        <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
          <UIDatePickerInput
            label={'Effective Date'}
            name='av_eff_date'
            id='av_eff_date'
            disabled={isAgeRestricted && !isStaff}
            onChange={evt => onEvEffDateChange(evt.target.value)}
            required={isPreviousPlicy && !previousPolicyHasEffDate ? false : true}
            autoComplete='off'
            showYearDropdown
            yearDropdownItemNumber={5}
            scrollableYearDropdown
            dateFormat='MM/dd/yyyy'
            selected={avEffDate}
            value={avEffDate && typeof avEffDate === 'object' ? moment(avEffDate).format('MM/DD/YYYY') : null}
            rules={{
              required: isPreviousPlicy && !previousPolicyHasEffDate ? false : true,
            }}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => {
              if (!(isPreviousPlicy && !previousPolicyHasEffDate)) {
                SalesTrackerStore.setValidity(idx, 'av_eff_date', validity.isValid)
              }
            }}
          />
          {(isPreviousPlicy && !previousPolicyHasEffDate ? true : false) && (
            <MDBIcon
              className='clear-icon'
              icon='times'
              onClick={() => {
                Policy.set('av_eff_date', null)
                setAvEffDate(null)
              }}
            />
          )}
        </MDBCol>
        {trackAcaLives ? (
          <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
            <UIInput
              label='Lives'
              name='aca_lives'
              type='number'
              disabled={isAgeRestricted && !isStaff}
              onChange={evt => {
                const filteredResult = preventInputUnicodeCharacters(evt.target.value)
                if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Lives')
                if (parseInt(filteredResult.fixedString) !== parseInt(Policy.get('aca_lives')))
                  Policy.set('aca_lives', filteredResult.fixedString)
              }}
              required={true}
              value={Policy.get('aca_lives')}
              rules={{ required: true, maxValue: 9999, minValue: 1 }}
              showValidity={SalesTrackerStore.showErrors}
              onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'aca_lives', validity.isValid)}
            />
          </MDBCol>
        ) : trackGroupLives ? (
          <MDBCol size='12' md='6' xl='4' className='av-input-wrapper'>
            <UIInput
              label='Group Members (Lives)'
              name='group_lives'
              type='number'
              disabled={isAgeRestricted && !isStaff}
              onChange={evt => {
                const filteredResult = preventInputUnicodeCharacters(evt.target.value)
                if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Lives')
                if (parseInt(filteredResult.fixedString) !== parseInt(Policy.get('group_lives')))
                  Policy.set('group_lives', filteredResult.fixedString)
              }}
              required={true}
              value={Policy.get('group_lives')}
              rules={{ required: true, maxValue: 9999, minValue: 1 }}
              showValidity={SalesTrackerStore.showErrors}
              onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'group_lives', validity.isValid)}
            />
          </MDBCol>
        ) : (
          <></>
        )}
      </MDBRow>
      <MDBRow>
        <PointOverrides
          Policy={Policy}
          policyIdx={idx}
          Fields={SalesTrackerStore.PointOverrideFields[idx]}
          carrierId={getCarrier()?.id() || ''}
          coverageId={getCoverage()?.id() || ''}
        />
      </MDBRow>
      {SalesTrackerStore.isAdmin() ? (
        <MDBRow>
          <PolicyCommission Policy={Policy} idx={idx} />
        </MDBRow>
      ) : (
        <></>
      )}
      <MDBRow>
        <MDBCol size='12' className='av-input-wrapper'>
          <UIInput
            label='Comments'
            name='av_comments'
            type='textarea'
            disabled={isAgeRestricted && !isStaff}
            onChange={evt => {
              const filteredResult = preventInputUnicodeCharacters(evt.target.value)
              if (filteredResult.hasIllegalCharacters) showToastForIllegalCharacters('Comments')
              if (filteredResult.fixedString !== Policy.get('av_comments'))
                Policy.set('av_comments', filteredResult.fixedString)
            }}
            value={Policy.get('av_comments')}
            showValidity={SalesTrackerStore.showErrors}
            onValidityChange={validity => SalesTrackerStore.setValidity(idx, 'av_comments', validity.isValid)}
          />
        </MDBCol>
      </MDBRow>
    </form>
  )
}

export default observer(AvPolicyForm)
